/* eslint-disable import/max-dependencies */
/* eslint-disable max-lines */
import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import React, {
  useState,
  useContext,
  createContext,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid, LoadingOverlay } from 'src/components/DataGrid';
import Delete from '@mui/icons-material/DeleteOutline';
import { GridColDef } from '@mui/x-data-grid';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/Add';
import Tooltip from '@mui/material/Tooltip';
import { useSearchParams } from 'react-router-dom';
import { StyledText } from 'src/components/StyledTypography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useAddBCN } from '../../hooks/multi-bcn';
import debounce from '@mui/material/utils/debounce';
import Autocomplete from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import {
  ResellerSearchResponse,
  useResellerSearch,
} from '../../hooks/deal-details';
import Typography from '@mui/material/Typography';
import { useAccount } from '@azure/msal-react';

const DEBOUCE_DELAY = 500;
const RESELLER_NUMBER_MAX_LENGTH = 8;
const RESELLER_NUMBER_PATTERN = /^[a-zA-Z0-9]*$/;

const BcnDropdownContext = createContext<{
  hasSelection: boolean;
  onAdd: () => void;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
}>({ hasSelection: false, onAdd: () => {} });

// Defined outside AddBcnPage so the reference is stable — avoids PaperComponent remounting on every render
function BcnDropdownPaper({
  children,
  ...rest
}: React.ComponentProps<typeof Paper>) {
  const { hasSelection, onAdd } = useContext(BcnDropdownContext);
  const translate = useTranslate();
  return (
    <Paper {...rest}>
      {children}
      <Divider />
      <Box onMouseDown={(e) => e.preventDefault()} p={1} textAlign="right">
        <Button
          data-testid="gds-vp-Bcnadd-add-btn"
          disabled={!hasSelection}
          onClick={onAdd}
          size="small"
          startIcon={<AddIcon />}
          variant="newContained"
        >
          {translate('VendorProgram.AddBCN')}
        </Button>
      </Box>
    </Paper>
  );
}

export type BCN = {
  Id: number;
  productMember: string;
  resellerName: string;
};

const renderTooltipCell = (item: { value?: React.ReactNode }) => {
  const value = <StyledText noWrap>{item.value}</StyledText>;
  return <Tooltip title={value}>{value}</Tooltip>;
};

export default function AddBcnPageNew(
  props: Readonly<{
    bcn_linking_level: string;
    customerSetId: string;
    dealId: string;
    handleClose: () => void;
  }>
) {
  const translate = useTranslate();
  const [searchParams] = useSearchParams();
  const importreferenceid = searchParams.get('importreferenceid');

  const containerRef = useRef<HTMLDivElement>(null);
  // Popper anchored to the full container (select + autocomplete) so it spans the whole bar
  const FullWidthPopper = useCallback(
    (popperProps: any) => (
      <Popper
        {...popperProps}
        anchorEl={containerRef.current}
        style={{ width: containerRef.current?.offsetWidth }}
      />
    ),
    []
  );

  const [searchType, setSearchType] = useState<'name' | 'number'>('number');
  const [searchInput, setSearchInput] = useState('');
  // Controlled input value — never let MUI reset it on selection so options stay visible
  const [acInputValue, setAcInputValue] = useState('');
  const [selectedResellers, setSelectedResellers] = useState<
    ResellerSearchResponse[]
  >([]);
  // Cache last fetched results so options never vanish during re-renders caused by selection
  const [stableOptions, setStableOptions] = useState<ResellerSearchResponse[]>(
    []
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [resellerNumberValidationMessage, setResellerNumberValidationMessage] =
    useState('');

  // Stable debounced setter so the function reference never changes
  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setSearchInput(value), DEBOUCE_DELAY),
    []
  );

  const searchOptions = [
    {
      label: translate('VendorDeals.ResellerSearchByNameLabel'),
      value: 'name' as const,
    },
    {
      label: translate('VendorProgram.BcnResellerSearchByNoLabel'),
      value: 'number' as const,
    },
  ];

  const resellerSearch = useResellerSearch({
    reseller_name: searchType === 'name' ? searchInput : '',
    reseller_no:
      searchType === 'number' && !resellerNumberValidationMessage
        ? searchInput
        : '',
  });

  // Persist the last non-empty API result so options are never lost mid-interaction
  useEffect(() => {
    if (resellerSearch.data && resellerSearch.data?.length > 0) {
      setStableOptions(resellerSearch.data);
    } else {
      setStableOptions([]);
    }
  }, [resellerSearch.data]);

  const getResellerOptionLabel = (option: ResellerSearchResponse) =>
    typeof option === 'string'
      ? option
      : `${option.reseller_name} ~ ${option.reseller_no}`;

  const isResellerSelected = (option: ResellerSearchResponse) =>
    selectedResellers.some((r) => r.reseller_no === option.reseller_no);

  const getResellerNumberValidationMessage = (value: string) =>
    value.length > RESELLER_NUMBER_MAX_LENGTH ||
    !RESELLER_NUMBER_PATTERN.test(value)
      ? translate('VendorDeals.ResellerSearchByNoValidation')
      : '';

  const [rows, setRows] = useState<BCN[]>([]);
  const handeldelteRow = (id: number) => {
    setRows((prevRows) => prevRows.filter((row) => row.Id !== id));
  };
  const columns: GridColDef<typeof rows[number]>[] = [
    {
      align: 'left',
      cellClassName: 'cellvender',
      field: 'productMember',
      flex: 1,
      headerName: translate('VendorProgram.AddBcnGridHeader'),
      renderCell: renderTooltipCell,
      sortable: false,
    },
    {
      align: 'left',
      cellClassName: 'cellvender',
      field: 'resellerName',
      flex: 2,
      headerName: 'Reseller',
      renderCell: renderTooltipCell,
      sortable: false,
    },
    {
      align: 'right',
      cellClassName: 'cellvender',
      field: '',
      flex: 1,
      headerName: '',
      renderCell: (item) => (
        <IconButton
          data-testid="gds-vp-vpn-list-delterow"
          onClick={() => handeldelteRow(item.row.Id)}
        >
          <Delete />
        </IconButton>
      ),
      sortable: false,
    },
  ];
  const [reprocessMsg, setReprocessMsg] = useState<string | undefined>();
  const addBCNList = useAddBCN();
  const userName = useAccount()?.username ?? '';
  const handleAddBCNlist = () => {
    // Flush any still-checked resellers that haven't been moved to the grid yet
    const pendingBcns = selectedResellers.map((r) => r.reseller_no);
    const gridBcns = rows.map((r) => r.productMember);
    const allBcns = [...gridBcns, ...pendingBcns].filter(Boolean);
    if (!allBcns.length) return;
    addBCNList
      .mutateAsync({
        BCN: allBcns.join(','),
        customerset_Id: props.customerSetId,
        deal_Id: props.dealId,
        editor: userName,
        importreferenceid: importreferenceid ?? '',
        source: props.bcn_linking_level,
      })
      .then(() => setReprocessMsg('Success'))
      .catch((error) => setReprocessMsg(error));
  };

  const handleAddBcns = () => {
    if (!selectedResellers.length) return;
    const newItems: BCN[] = selectedResellers.map((reseller, index) => ({
      Id: rows.length + index,
      productMember: reseller.reseller_no,
      resellerName: reseller.reseller_name,
    }));
    setRows((prevItem) => [...prevItem, ...newItems]);
    setSelectedResellers([]);
    setSearchInput('');
    setAcInputValue('');
    setStableOptions([]);
    setDropdownOpen(false);
  };

  return (
    <Dialog
      data-testid="Add-bcnList-dialog"
      fullWidth
      maxWidth="md"
      onClose={props.handleClose}
      open={true}
    >
      <DialogTitle data-testid="vp-bcnList-dialog-title">
        <Stack>{translate('VendorProgram.BcnListTitle')}</Stack>
        <IconButton
          data-testid="add-bcn-close-btn"
          onClick={props.handleClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {reprocessMsg === 'Success' ? (
        <Box
          alignItems={'center'}
          display={'flex'}
          flexDirection={'column'}
          height={'50vh'}
          justifyContent={'center'}
        >
          <CheckCircleIcon sx={{ color: '#1976d2', fontSize: 64 }} />
          <Typography variant="h1">
            {translate('VendorProgram.AddBcnMessage')}
          </Typography>
        </Box>
      ) : (
        <Stack>
          <DialogContent>
            <Stack
              alignItems="self-end"
              direction="row"
              ref={containerRef}
              sx={{ width: '100%' }}
            >
              <TextField
                SelectProps={{ MenuProps: { elevation: 1 } }}
                data-testid="gds-vp-bcn-search-type-select"
                select
                sx={{
                  '.MuiInputBase-input': {
                    color: 'inherit !important',
                    lineHeight: '23px !important',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    borderBottomRightRadius: 0,
                    borderRight: 'none',
                    borderTopRightRadius: 0,
                  },
                  minWidth: 160,
                }}
                value={searchType}
              >
                {searchOptions.map((opt) => (
                  <MenuItem
                    dense
                    key={opt.value}
                    onClick={() => {
                      setSearchType(opt.value);
                      setSearchInput('');
                      setAcInputValue('');
                      setStableOptions([]);
                      setSelectedResellers([]);
                      setResellerNumberValidationMessage('');
                    }}
                    sx={{ px: 1 }}
                    value={opt.value}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <BcnDropdownContext.Provider
                value={{
                  hasSelection: selectedResellers.length > 0,
                  onAdd: handleAddBcns,
                }}
              >
                <Autocomplete
                  ListboxProps={{
                    style: {
                      maxHeight: '180px',
                      overflowX: 'hidden',
                      overflowY: 'auto',
                    },
                  }}
                  PaperComponent={BcnDropdownPaper}
                  PopperComponent={FullWidthPopper}
                  data-testid="gds-vp-bcn-reseller-searchbar"
                  disableCloseOnSelect
                  filterOptions={(options) => options}
                  filterSelectedOptions={false}
                  fullWidth
                  getOptionLabel={getResellerOptionLabel}
                  inputValue={acInputValue}
                  isOptionEqualToValue={(option, value) =>
                    option.reseller_no === value.reseller_no
                  }
                  multiple
                  noOptionsText={
                    resellerNumberValidationMessage ? (
                      <Typography color="error">
                        {resellerNumberValidationMessage}
                      </Typography>
                    ) : (
                      'No options'
                    )
                  }
                  onChange={(_, value) => setSelectedResellers(value)}
                  onClose={(_, reason) => {
                    if (reason !== 'selectOption') setDropdownOpen(false);
                  }}
                  onInputChange={(_, value, reason) => {
                    // Only respond to actual user typing — ignore 'reset' fired after selection
                    if (reason === 'input') {
                      setAcInputValue(value);
                      if (value.length > 0) setDropdownOpen(true);
                      else setDropdownOpen(false);
                      if (!value.includes('~')) {
                        const validationMessage =
                          searchType === 'number'
                            ? getResellerNumberValidationMessage(value)
                            : '';
                        setResellerNumberValidationMessage(validationMessage);
                        if (validationMessage) {
                          setSearchInput('');
                          setStableOptions([]);
                          setSelectedResellers([]);
                        } else {
                          debouncedSetSearch(value);
                        }
                      }
                    }
                  }}
                  onOpen={() => {
                    if (acInputValue.length > 0) setDropdownOpen(true);
                  }}
                  open={dropdownOpen && acInputValue.length > 0}
                  options={stableOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              className="BcnSearchIcon"
                              fontSize="small"
                            />
                          </InputAdornment>
                        ),
                      }}
                      data-testid="gds-vp-bcn-reseller-textfield"
                      error={Boolean(resellerNumberValidationMessage)}
                      placeholder={
                        searchType === 'name'
                          ? translate(
                              'VendorDeals.ResellerSearchByNamePlaceholder'
                            )
                          : translate(
                              'VendorDeals.ResellerSearchByNoPlaceholder'
                            )
                      }
                    />
                  )}
                  renderOption={(optionProps, option) => (
                    <li {...optionProps} key={option.reseller_no}>
                      <Checkbox
                        checked={isResellerSelected(option)}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                      />
                      <Stack
                        alignItems="center"
                        direction="row"
                        justifyContent="space-between"
                        width="100%"
                      >
                        <StyledText>{option.reseller_name}</StyledText>
                        <StyledText>{option.reseller_no}</StyledText>
                      </Stack>
                    </li>
                  )}
                  renderTags={() => null}
                  sx={{
                    '&:focus-within .BcnSearchIcon': { fill: 'primary.main' },
                    '.MuiAutocomplete-noOptions': {
                      color: resellerNumberValidationMessage
                        ? 'error.main'
                        : undefined,
                    },
                    '.MuiInputBase-root': {
                      borderBottomLeftRadius: 0,
                      borderTopLeftRadius: 0,
                    },
                  }}
                  value={selectedResellers}
                />
              </BcnDropdownContext.Provider>
            </Stack>
            <Box mt={2}>
              <DataGrid
                autoHeight
                columnBuffer={columns.length}
                columns={columns}
                components={{ LoadingOverlay: LoadingOverlay }}
                data-testid="gds-vp-AddBcn-list-dataGrid"
                disableColumnFilter
                disableColumnMenu
                disableColumnSelector
                disableSelectionOnClick
                getRowHeight={() => 'auto'}
                getRowId={(id) => id.Id}
                hideFooterPagination={true}
                rows={rows as any}
                sx={{
                  '.MuiDataGrid-columnHeader--moving': {
                    backgroundColor: 'transparent',
                  },
                  '.MuiDataGrid-columnHeaderTitle': {
                    whiteSpace: 'normal',
                  },
                  '.MuiDataGrid-row': {
                    minHeight: '52px !important',
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid="gds-vp-addvpn-add-btnpoup-btn"
              disabled={rows.length === 0}
              onClick={handleAddBCNlist}
              startIcon={<AddIcon />}
              variant="newContained"
            >
              {translate('VendorProgram.AddBcnListbtn')}
            </Button>
          </DialogActions>
        </Stack>
      )}
    </Dialog>
  );
}
